import * as React from 'react';

import { EditIcon } from '../../../assets/icons/general/edit-4';
import { cn } from '../../../lib/utils';
import { Input } from '../input';

import { editableInputVariants, editableInputIconVariants } from './variants';

export interface EditableInputProps extends Omit<
  React.ComponentProps<typeof Input>,
  'onChange'
> {
  value?: string;
  onChange?: (value: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  defaultEditable?: boolean;
  displayValue?: string;
}

const EditableInput = React.forwardRef<HTMLInputElement, EditableInputProps>(
  (
    {
      value = '',
      onChange,
      onEditStart,
      onEditEnd,
      defaultEditable = false,
      displayValue,
      disabled = false,
      placeholder = 'Click to edit',
      className,
      type = 'text',
      ...props
    },
    ref,
  ) => {
    const [isEditing, setIsEditing] = React.useState(defaultEditable);
    const [internalValue, setInternalValue] = React.useState(value);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    React.useEffect(() => {
      setInternalValue(value);
    }, [value]);

    React.useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isEditing]);

    const handleEditClick = () => {
      if (disabled) return;
      setIsEditing(true);
      onEditStart?.();
    };

    const handleBlur = () => {
      setIsEditing(false);
      onEditEnd?.();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange?.(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleBlur();
      }
      if (e.key === 'Escape') {
        setInternalValue(value);
        setIsEditing(false);
        onEditEnd?.();
      }
    };

    if (isEditing) {
      return (
        <Input
          ref={inputRef}
          type={type}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={className}
          {...props}
        />
      );
    }

    return (
      <div
        className={cn(
          editableInputVariants({ mode: 'view', disabled }),
          className,
        )}
        onClick={handleEditClick}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleEditClick();
          }
        }}
      >
        <span
          className={cn(
            'flex-1 truncate',
            !internalValue && 'text-muted-foreground',
          )}
        >
          {displayValue || internalValue || placeholder}
        </span>
        <EditIcon
          className={cn(
            'h-4 w-4',
            editableInputIconVariants({ mode: 'view', disabled }),
          )}
        />
      </div>
    );
  },
);

EditableInput.displayName = 'EditableInput';

export { EditableInput };
